import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Printer, Search, X } from 'lucide-react';
import api from '../services/api';
import { Bloques, normalizar, parsearManual } from '../ayuda/markdown';

// Manual de usuario dentro de la app (#129). Se abre en otra pestaña desde
// el botón (?) de cada pantalla o desde "Ayuda" del menú, así no se pierde
// lo que se estaba haciendo. El texto sale de docs/MANUAL_OPERARIO.md.
export default function Ayuda() {
  const [manual, setManual] = useState(null);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [destacada, setDestacada] = useState(window.location.hash.slice(1));

  useEffect(() => {
    document.title = 'Ayuda · Corporación G&F';
    api.get('/ayuda/manual')
      .then(({ data }) => setManual(parsearManual(data.texto)))
      .catch(() => setError('No se pudo cargar el manual. Vuelve a intentarlo en un momento.'));
  }, []);

  // Al llegar con #seccion (desde el (?) de una pantalla) se baja a esa
  // sección y se marca un momento para ubicarla de un vistazo.
  useEffect(() => {
    const irA = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      setBusqueda('');
      setDestacada(id);
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
    };
    if (manual) irA();
    window.addEventListener('hashchange', irA);
    return () => window.removeEventListener('hashchange', irA);
  }, [manual]);

  useEffect(() => {
    if (!destacada) return undefined;
    const t = setTimeout(() => setDestacada(''), 2500);
    return () => clearTimeout(t);
  }, [destacada]);

  const palabras = useMemo(
    () => normalizar(busqueda).split(/\s+/).filter((p) => p.length > 1),
    [busqueda]
  );
  // Al buscar se vuelve arriba, para ver la primera sección que coincide.
  useEffect(() => {
    if (palabras.length) window.scrollTo({ top: 0 });
  }, [palabras]);

  const visibles = useMemo(() => {
    if (!manual) return [];
    if (!palabras.length) return manual.secciones;
    return manual.secciones.filter((s) => palabras.every((p) => s.textoBusqueda.includes(p)));
  }, [manual, palabras]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-700">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur print:static print:border-0">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-center gap-2">
            <BookOpen size={22} className="text-primary-600" />
            <p className="text-lg font-bold text-slate-800">{manual?.titulo || 'Manual de usuario'}</p>
          </div>
          <div className="relative flex-1 print:hidden">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              autoFocus
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en el manual (ej. transferencia, cierre, tasa)"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-9 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                title="Limpiar búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {manual && (
            <select
              value=""
              onChange={(e) => { window.location.hash = e.target.value; }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600 md:hidden print:hidden"
            >
              <option value="" disabled>Ir a una sección…</option>
              {visibles.map((s) => <option key={s.id} value={s.id}>{s.titulo}</option>)}
            </select>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            title="Imprimir / PDF"
            className="hidden items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 sm:flex print:hidden"
          >
            <Printer size={16} /> Imprimir
          </button>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6">
        {manual && (
          <nav className="sticky top-20 hidden max-h-[calc(100vh-6rem)] w-60 flex-shrink-0 overflow-y-auto md:block print:hidden">
            <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {palabras.length ? `${visibles.length} ${visibles.length === 1 ? 'sección' : 'secciones'}` : 'Contenido'}
            </p>
            {visibles.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block truncate rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-white hover:text-primary-600"
              >
                {s.titulo}
              </a>
            ))}
          </nav>
        )}

        <main className="min-w-0 flex-1 space-y-4">
          {error && <p className="rounded-xl bg-white p-6 text-red-600 shadow-sm">{error}</p>}
          {!manual && !error && <p className="p-6 text-slate-500">Cargando manual…</p>}
          {manual && palabras.length > 0 && visibles.length === 0 && (
            <p className="rounded-xl bg-white p-6 text-slate-500 shadow-sm">
              Nada en el manual coincide con “{busqueda}”. Prueba con otra palabra.
            </p>
          )}
          {visibles.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className={`scroll-mt-36 space-y-3 sm:scroll-mt-24 rounded-xl bg-white p-5 text-[15px] leading-relaxed shadow-sm transition-shadow duration-500 sm:p-6 print:break-inside-avoid-page print:shadow-none ${
                destacada === s.id ? 'ring-2 ring-primary-400' : ''
              }`}
            >
              <h2 className="text-xl font-bold text-slate-800">{s.titulo}</h2>
              <Bloques bloques={s.bloques} palabras={palabras} />
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}
